import React, { useState, useEffect, Fragment } from 'react';
import { connect } from 'react-redux';
import lodash from 'lodash';
import uuid from 'node-uuid';
import {DetailDatePicker, DetailInput, DetailSelect} from './EventDetailsControls';
import { formatDate } from 'pages/events/util';
import { Conditional } from 'common/components/layout';


import { loadActiveEventType } from '../actions/events';
import { binderInstitutionIdSelector } from 'common/selectors';

function CustomEventFields(props) {
    const [showOtherInput, setShowOtherInput] = useState({});
    useEffect(() => {
        props.loadActiveEventType(props.institutionId, props.eventType.id);
    }, [props.eventType.id]);

    return (
        <div data-cy="CustomEventFields">
            {props.activeEventType.fields.map(field => {
                return viewToRenderFunc(field.view)({
                    updateFieldValue: props.updateFieldValue,
                    details: props.details,
                    field,
                    showOtherInput: showOtherInput[field.name],
                    setShowOtherInput: (value) => setShowOtherInput({...showOtherInput, [field.name]: value}),
                });
            })}
        </div>
    );
}

function viewToRenderFunc(view) {
    switch (view) {
        case 'DatePicker':
            return renderDatePickerComponent;
        case 'SingleSelect':
            return renderSingleSelect;
        case 'ShortText':
            return renderShortTextInputComponent;
        default:
            return () => { return null; };
    }
}

function getFieldValue(details, fieldName) {
    const eventFieldValue = lodash.get(details, fieldName, null);

    if (eventFieldValue || eventFieldValue === '') {
        return eventFieldValue;
    }

    if (!lodash.isEmpty(details.fields)) {
        const detailField = details.fields.find(f => f.name === fieldName);
        if (detailField) {
            return detailField.value;
        } else {
            return null;
        }
    }

    return null;
}

function renderDatePickerComponent({ updateFieldValue, details, field }) {
    const eventFieldValue = getFieldValue(details, field.name);

    return (
        <DetailDatePicker 
            key={uuid.v4()}
            label={field.displayName}
            value={eventFieldValue}
            onSelect={value => updateFieldValue(field.name, formatDate(value))}
            data-cy="CustomEventFields-DetailDatePicker"
        />
    );
}

function renderSingleSelect({ updateFieldValue, details, field, showOtherInput, setShowOtherInput }) {
    const options = field.options.values.map(value => ({ label: value, value: value }));

    const eventFieldValue = getFieldValue(details, field.name);

    const otherValue = 'other';
    if (field.enableOtherOption) {
        options.push({ label: 'Other', value: otherValue });
        // note: if the other input is not currently showing and the field value is not one of the options
        // then show the other input
        if (!showOtherInput && eventFieldValue && !options.some(option => option.value === eventFieldValue)) {
            setShowOtherInput(true);
        }
    }
    return (
        <Fragment key={field.name}  data-cy="CustomEventFields-Fragment">
            <DetailSelect
                key={field.name}
                label={field.displayName}
                value={showOtherInput ? otherValue : eventFieldValue}
                items={options}
                onSelect={value => {
                    if (value == otherValue) {
                        setShowOtherInput(true);
                        updateFieldValue(field.name, '');
                    } else {
                        setShowOtherInput(false);
                        updateFieldValue(field.name, value);
                    }
                }}
            />
            <Conditional condition={showOtherInput}>
                <DetailInput
                    key={`${field.name}-other`}
                    type='textarea'
                    title={field.displayName}
                    placeholder={'Type description here...'}
                    required={true}
                    maxLength={100}
                    value={eventFieldValue}
                    onChange={e => updateFieldValue(field.name, e.target.value)}
                />
            </Conditional>
        </Fragment>
    );
}

function renderShortTextInputComponent({ updateFieldValue, details, field }) {
    const eventFieldValue = getFieldValue(details, field.name);

    return (
        <DetailInput
            key={`long-text-input-${field.name}`}
            title={field.displayName}
            placeholder={''}
            type={'textarea'}
            required={false}
            value={eventFieldValue}
            onChange={e => updateFieldValue(field.name, e.target.value)}
            maxLength={100}
            data-cy="CustomEventFields-DetailInput"
        />
    );
}

export default connect((state, props) => {
    const institutionId = binderInstitutionIdSelector(state);
    const defaultActiveEventType = {fields: []};
    const activeEventType = lodash.get(state, 'studyEvents.eventData.activeEventType', defaultActiveEventType);

    return {
        institutionId,
        activeEventType: lodash.isEmpty(activeEventType) ? defaultActiveEventType : activeEventType,
    };
}, {
    loadActiveEventType,
    renderDatePickerComponent
})(CustomEventFields);
